/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/
import { Component, inject, Injector, OnDestroy, Type } from '@angular/core';
import { Observable, Subscription } from 'rxjs';
import { BankAccount } from '@src/app/model/bankAccount';
import { BCode } from '@src/app/model/bcode';
import { Period } from '@src/app/model/period';
import { Schedule } from '@src/app/model/schedule';
import { Unit } from '@src/app/model/unit';
import { User } from '@src/app/model/user';
import { FormPicklistComponent } from '@src/app/shared/form/form-picklist/form-picklist.component';
import { IsNarrowService } from '@src/app/shared/is-narrow.service';
import { TxnImportChecker } from '@src/app/modules/txn/txn.service';
import { BM5ParserService } from '@src/app/modules/importer/BlockMan/BM5ParserService';

import { ImportDoc, ImportDocNote } from '@src/app/modules/importer/ImportDoc';
import { ConfirmDialogService } from '@src/app/shared/dialogs/confirmDialog';
import { CurrentUserService } from '@src/app/modules/user/current-user.service';
import { BM4ParserService } from '@src/app/modules/importer/BlockMan/BM4Parser';
import { BM2ParserService } from '@src/app/modules/importer/BlockMan/BM2Parser';
import { BM1ParserService } from '@src/app/modules/importer/BlockMan/BM1Parser';

import { ByteCountPipe } from '@src/app/shared/pipes/byte-count.pipe';
import { UploadProgressComponent } from '@src/app/shared/form/file/upload-progress/upload-progress.component';
import { UploadDragAndDropDirective } from '@src/app/shared/form/file/file-upload/upload-drag-and-drop.directive';
import { CtlHolderComponent } from '@src/app/shared/form/ctl-holder/ctl-holder.component';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { NgTemplateOutlet, Location } from '@angular/common';

import { UnitParserService } from '@src/app/modules/importer/General/UnitParserService';
import { CSVImporter } from '@src/app/modules/importer/import-page/csv-importer';
import { PeopleParserService } from '@src/app/modules/importer/General/PeopleParserService';
import { ArParserService } from '@src/app/modules/importer/General/ArParserService';
import { ImportOption } from '@src/app/modules/importer/import-page/ImportOption';
import { MessageService } from '@src/app/shared/message.service';
import { HttpClient } from '@angular/common/http';
import { TenantService } from '@src/app/shared/tenant.service';
import { AbstractObject } from '@src/app/model/abstract-object';
import { TrialBalanceParserService } from '@src/app/modules/importer/General/TrialBalanceParserService';
import { ImportRow } from '@src/app/modules/importer/ImportRow';
import { ImportParser } from '@src/app/modules/importer/import-page/Import-parser-interface';
import { ActivatedRoute, NavigationStart, Router } from '@angular/router';
import { ApParserService } from '@src/app/modules/importer/General/ApParserService';
import { XeroConnectorComponent } from '@src/app/modules/importer/import-page/xero-connector/xero-connector.component';
import { BudgetCodeParserService } from '@src/app/modules/importer/General/BudgetCodeParserService';
import { SupplierParserService } from '@src/app/modules/importer/General/SupplierParserService';
import { JournalEntryParser } from '@src/app/modules/importer/General/JournalEntryParserService';
import { ImportPageNavRoute } from '@src/app/routes/app-router';
import { Downloader } from '@src/app/shared/downloader';
import { MatTooltip } from "@angular/material/tooltip";
import { XeroAllParserService } from '@src/app/modules/importer/General/XeroAllParserService';
import { DownloadTemplateAction } from '@src/app/modules/importer/import-page/download-template-action';

export class ImportFile extends AbstractObject {
    result: string;
    status: string;
}
@Component({
    selector: 'app-import-page',
    templateUrl: './import-page.component.html',
    styleUrls: ['./import-page.component.scss'],
    imports: [NgTemplateOutlet, MatButtonModule, MatIconModule, CtlHolderComponent, UploadDragAndDropDirective,
        UploadProgressComponent, ByteCountPipe, MatIconModule, XeroConnectorComponent, MatIconModule, MatTooltip]
})
export class ImportPageComponent implements OnDestroy {
    private cds = inject(ConfirmDialogService);
    currentUserSvc = inject(CurrentUserService);
    private msgSvc = inject(MessageService);
    private http = inject(HttpClient);
    private activatedRoute = inject(ActivatedRoute);
    private location = inject(Location);

    options: ImportOption[] = [];
    currentOption: ImportOption = null;
    parsing = false;
    private injector = inject(Injector);
    subscriptions: Subscription[] = [];

    importFiles: ImportFile[] = [];

    importerField = FormPicklistComponent.make('Import Type', 'importer', '', {
        refreshes: [this.refreshCurrentOption.bind(this)]
    });
    importerControl = this.importerField.makeControl();

    files: { progress: number, name: string, size: number, failed: boolean }[];

    importDoc: ImportDoc = new ImportDoc();
    isVeryNarrow = false;
    units: Unit[];
    currentUser: User;

    schedules: Schedule[];
    bCodes: BCode[];
    bankAccounts: BankAccount[];
    periods: Period[];
    existingData: TxnImportChecker;

    working = false;
    validated = false;

    ready = false;

    showRowDetails = 100;

    constructor() {
        const narrowSvc = inject(IsNarrowService);
        const routerSvc = inject(Router);
        const activatedRoute = this.activatedRoute;


        this.subscriptions.push(this.currentUserSvc.getCurrentUser().subscribe(user => {
            if (this.currentUser && this.currentUser.current_team_id !== user.current_team_id) {
                window.alert('You have changed team process cannot continue ');
                window.location.reload();
            }
            this.currentUser = user;
        }));

        this.refreshOptions();
        this.subscriptions.push(routerSvc.events.subscribe( (evt) => {
            if (evt instanceof NavigationStart) {
                this.refreshOptions();
            }
        }));

        this.subscriptions.push(activatedRoute.paramMap.subscribe( params => {
            const itemId = +params.get('itemId');
            if ( itemId !== this.currentOption?.id) {
                const newO = this.options.find( o => o.id === itemId);
                if (newO) {
                    this.importerControl.setValue(itemId);
                    this.refreshCurrentOption(newO);
                    console.log('Set current option', {newO, itemId});
                }
            }
        }));

        this.importerField.setPicklistItems(this.options);

        this.subscriptions.push(narrowSvc.detectVeryNarrow().subscribe(
            result => { this.isVeryNarrow = result; }
        ));
    }

    fresh(type: Type<ImportParser>) {
        const freshInjector = Injector.create({providers: [type], parent: this.injector});
        const parser = freshInjector.get(type)
        return parser;
    }

    downloadTemplate() {
        new DownloadTemplateAction().action(null);
    }

    getUnitParserOption() {
        return {
            id: 0, name: 'Unit Import',
            parser: this.fresh(UnitParserService),
            processor: null,
            sampleFile: './assets/sample-files/units.csv',
        }
    }

    refreshOptions() {
        if (this.currentUser.inAgency) {
            this.options = [
                { id: 1001, name: 'Suppliers', parser: this.fresh(SupplierParserService), processor: null },
                { id: 1010, name: 'Xero Full Export', parser: this.fresh(XeroAllParserService), processor: null }
                //{ id: 101, name: 'Request Imports', parser: requestParser, processor: requestProcessor },
                //{ id: 511, name: 'Blockman OMCs Import', parser: bmOmcParser, processor: bmOmcProcessor },
                //{ id: 512, name: 'Blockman Supplier (Creditor) Import', parser: bmSupplierParser, processor: bmSupplierProcessor },
                //{ id: 513, name: 'Blockman Emergency Contact Import', parser: bmEmergencyParser, processor: null }
            ];
        } else {
            this.options = [
                this.getUnitParserOption(),
                {
                    id: 1, name: 'Contact Import', parser: this.fresh(PeopleParserService), processor: null,
                    sampleFile: './assets/sample-files/people.csv'
                },
                { id: 5, name: 'Accounts Receivable (Charges/Receipts)', parser: this.fresh(ArParserService), processor: null,
                    sampleFile: './assets/sample-files/charges.csv' },

                { id: 6, name: 'Accounts Payable (Purchases/Payments)', parser: this.fresh(ApParserService), processor: null,
                    sampleFile: './assets/sample-files/purchases.csv'
                },
                {
                    id: 10, name: 'Budget Code / Nominal Account Import',
                    parser: this.fresh(BudgetCodeParserService), processor: null,
                    sampleFile: './assets/sample-files/trialBalance.csv'
                },
                { id: 20, name: 'Initial Trial Balance Import',
                        parser: this.fresh(TrialBalanceParserService), processor: null,
                    sampleFile: './assets/sample-files/trialBalance.csv'
                },
                { id: 30, name: 'Journal Entry Import',
                        parser: this.fresh(JournalEntryParser), processor: null,
                        sampleFile: './assets/sample-files/journals.csv'
                },                
                { id: 501, name: 'Blockman 1 Unit Data', parser: this.fresh(BM1ParserService), processor: null },
                { id: 502, name: 'Blockman 2 Unit Percentages Import', parser: this.fresh(BM2ParserService), processor: null },
                { id: 503, name: 'Blockman 4 Unit Data Import', parser: this.fresh(BM4ParserService), processor: null, delimiter: '|' },
                // { id: 504, name: 'Blockman BM5 Transaction Import', parser: BM5ParserSvc, processor: BM5ProcessorSvc, delimiter: '|' },

                { id: 524, name: 'Blockman BM5 Transaction Import', parser: this.fresh(BM5ParserService), processor: null },
                { id: 1010, name: 'Xero Full Export', parser: this.fresh(XeroAllParserService), processor: null }
                //{ id: 505, name: 'Blockman Creditors Details Listing CSV Import', parser: bmPIParserSvc, processor: bmPIProcessorSvc },
            ];
        }
        if (this.currentOption) {
            // Make sure to use the fresh one, not the previous
            this.currentOption = this.options.find( o => o.id === this.currentOption.id);
        }
        this.ready = true;
    }
    refreshCurrentOption(o: ImportOption = this.currentOption) {
        this.currentOption = o;
        this.importFiles = [];
        if (o.id) {
            this.location.replaceState(ImportPageNavRoute.getIdUrl(o.id));
        }
        if (this.currentOption.parser.batchType) {
            const url = TenantService.ajaxPath + 'importFile/' + this.currentOption.parser.batchType;
            this.http.get<ImportFile[]>(url).subscribe(result => this.importFiles = result);
        }
    }

    getImportData(file: ImportFile, $event: Event) {
        $event.stopImmediatePropagation();
        $event.preventDefault();
        const url = `${TenantService.ajaxPath}importFile/attach/${file.id}`;
        this.http.get(url, { 
            headers: { 'Accept': 'application/octet-stream' }, 
            observe: 'response', 
            responseType: 'arraybuffer' 
        }).subscribe(fileResponse => {
            this.msgSvc.show('Data retrieved to your downloads')
            const contentType = fileResponse.headers.get('our-mime-type');
            const blob = new Blob([fileResponse.body], { type: contentType });
            const fileName = fileResponse.headers.get('our-file-name');
            const file = new File([blob], fileName, { type: contentType });

            const a = document.createElement("a");
            document.body.appendChild(a);
            a.style.display = "none";

            const fileURL = URL.createObjectURL(file);

            a.href = fileURL;
            a.download = fileName;
            a.click();
            window.URL.revokeObjectURL(fileURL);
        }, failure => {
            this.msgSvc.showError(failure, 'retrieving import data');
        });        
    }


    /**
   * handle file dropped on control
   */
    onFileDropped($event) {
        this.uploadFiles($event);
    }

    /**
     * handle file user selected from file System browsing
     */
    fileBrowseHandler(files) {
        this.uploadFiles(files);
    }

    uploadFiles($event) {

        const upFile = $event[0];
        this.parsing = true;
        this.refreshOptions(); // fresh parser per file - parsers carry column-mapping / match state between loads
        this.subscriptions.push(this.currentOption.parser.setUp().subscribe(
            () => {
                const reader = new FileReader();
                reader.onload = this.parseFile.bind(this);
                reader.readAsText(upFile);
                setTimeout(() => this.parsing = false, 100);
            }
        ));
    }

    selectAgain() {
        console.log('Resetting The Import Document');
        this.refreshOptions();
        this.importDoc = new ImportDoc();
    }

    ngOnDestroy(): void {
        console.log('Destroy');
        this.subscriptions.forEach(s => s.unsubscribe());
    }

    deleteRow(row: ImportRow) {
        const msg = $localize `Are you sure you want to delete this row and continue with import?`;
        this.cds.open($localize `Delete This Row`, msg, () => {
            this.currentOption.parser.deleteRow(row);
        });
    }

    setupAndParse(parms: {items: string[][], notes?: ImportDocNote[]}) {
        this.refreshOptions(); // fresh parser per load - see uploadFiles
        this.subscriptions.push(this.currentOption.parser.setUp().subscribe( () => {
            this.parseRows(parms.items, parms.notes);
        }));
    }

    parseRows(rows: string[][], notes?: ImportDocNote[]) {
        console.log(rows);
        const ni = new ImportDoc();
        console.log(ni);
        this.importDoc = this.currentOption.parser.parseRows(rows);
        if (notes) {
            notes.forEach ( n => this.importDoc.addNoteItem(n))
        }
        this.importDoc.finalise();
    }

    parseFile(pe: ProgressEvent) {
        try {
            console.log('Parsing input file');
            const csv = (pe.target as FileReader).result;
            const rows = (new CSVImporter()).csvToArray(csv, (this.currentOption.delimiter ? this.currentOption.delimiter : ','));
            this.parseRows(rows);
        } catch (e) {
            this.cds.alert('Fatal Error', 'Could not parse file', this.selectAgain.bind(this));
            console.error(e);
        }

        //const parser = new BM5Parser();
        //this.importDoc = parser.parseRows(rows, this.units, this.schedules, this.bCodes, this.existingData);
    }

    postData() {
        this.currentOption.processor.postData();
    }

    showValidateButton() {
        if (!this.importDoc.hasErrors()
            && this.importDoc.getRows().length > 0
            && !this.importDoc.needsFix()
            && !this.validated) {
            let theReturn = true;
            this.importDoc.getInputs().forEach(ii => {
                ii.getControls().forEach(ic => {
                    if (!ic.valid) {
                        theReturn = false;
                    }
                })
            })
            return theReturn;
        } else {
            return false;
        }
    }

    downLoadData() {
        const data = [];
        this.importDoc.getRows().forEach(row => {
            const rowOut = [];
            row.getFields().forEach (f => {
                rowOut.push(f.getValue());
            })
            data.push(rowOut);
        });
        const downloader = new Downloader();
        downloader.downloadCSV(this.currentOption.name, data);
    }    

    validateData() {
        let errors = 0;
        for (const ii of this.importDoc.getInputs()) {
            for (const f of ii.getFields()) {
                if (f.control.valid) {
                    console.log('OK');
                } else {
                    console.log('Error');
                    f.control.markAsDirty();
                    f.control.markAsTouched();
                    errors += 1;
                }
            }
        }

        if (errors === 0) {
            if (this.currentOption.parser.postToServer) {
                this.postOnParser();
            } else {
                this.postOnProcessor();
            }
        }
    }

    postOnParser() {
        this.working = true;
        this.currentOption.parser.postToServer().subscribe(success => {
            //const cnt = success.itemList.length
            console.log(success);
            //const msg = $localize `You have successfully imported ${cnt} records`;
            if (success) {
                this.importDoc.clearInputs();
                this.importDoc.setRows([]);
                this.importDoc.completed = success.itemList?.length;
                if (this.currentOption.parser.batchType) {
                    this.msgSvc.show('Import request successfully submitted');
                    this.refreshOptions();
                    this.refreshCurrentOption();
                }
            }
            this.working = false;
        }, failure => {
            console.error('Import failed', failure);
            const msg = failure?.message ? failure.message : '';
            this.cds.open('Cannot Import File', `File cannot be imported. ${msg}. Try loading another?`, () => {
                this.selectAgain();
            })
        });
    }

    postOnProcessor() {
        this.subscriptions.push(this.currentOption.processor.setUp().subscribe(() => {
            console.log('Setup complete, pre processing data');
            this.currentOption.processor.getData(this.importDoc);
            this.currentUserSvc.httpGetCurrentUser(); // Force a refresh so we can see we are in right team...
            this.importDoc.finalise();
            console.log('Done');
        }));
        this.working = false;
        this.validated = true;
    }

    canDeactivate(): Observable<boolean> | Promise<boolean> | boolean {
        if (this.importDoc.getRows().length > 0) {
            return confirm('Do you wish to abandon the import?');
        }
        return true;
    }
}
